<template>
  <div class="card">
    <h3 class="card-title">{{ $t('preview.title') }}</h3>
    
    <!-- 悬浮提示 -->
    <div v-if="hover.visible" class="hover-tooltip" :style="{ left: hover.x + 'px', top: hover.y + 'px' }">
      <div class="swatch" :style="{ backgroundColor: hover.hex }"></div>
      <div class="meta">
        <div class="name">{{ hover.title }}</div>
        <div class="hex">{{ hover.hex.toUpperCase() }}</div>
        <div v-if="hover.extra" class="extra">{{ hover.extra }}</div>
      </div>
    </div>

    <div v-if="!canGeneratePreview" class="no-preview">
      <p v-if="!hasImage">{{ $t('preview.noImage') }}</p>
      <p v-else-if="!hasExtractedColors">{{ $t('preview.noColors') }}</p>
    </div>
    
    <div v-else class="preview-section">
      <div class="preview-controls">
        <div class="control-group">
          <label for="previewPixelSize">{{ $t('preview.pixelSize') }}</label>
          <select id="previewPixelSize" v-model="pixelSize" @change="updatePreview">
            <option value="2">2px</option>
            <option value="4">4px</option>
            <option value="8">8px</option>
            <option value="16">16px</option>
            <option value="32">32px</option>
          </select>
        </div>
        
        <div class="control-group">
          <label for="previewScale">{{ $t('preview.scale') }}</label>
          <select id="previewScale" v-model="scale" @change="updatePreview">
            <option value="1">1:1</option>
            <option value="2">2:1</option>
            <option value="4">4:1</option>
            <option value="8">8:1</option>
          </select>
        </div>
        
        <div class="download-controls">
          <button class="btn-primary" @click="downloadOriginalPreview">
            {{ $t('preview.downloadOriginal') }}
          </button>
          <button 
            class="btn-primary" 
            @click="downloadDisplayImage"
            :disabled="!displayImage"
          >
            {{ hasCustomColors ? $t('preview.downloadMatched') : $t('preview.downloadOriginalMatched') }}
          </button>
          <button 
            class="btn-primary" 
            @click="downloadExcelPixelArt"
            :title="$t('preview.downloadExcel')"
          >
            {{ $t('preview.downloadExcel') }}
          </button>
        </div>
      </div>
      
      <div class="preview-container">
        <div class="preview-info">
          <p>{{ $t('preview.gridSize') }} <strong>{{ gridSize.width }} × {{ gridSize.height }}</strong></p>
          <p>{{ $t('preview.previewSize') }} <strong>{{ previewWidth }} × {{ previewHeight }}</strong></p>
          <p>{{ $t('preview.pixelSize') }} <strong>{{ pixelSize }}px</strong></p>
          <p>{{ $t('preview.customColorsCount') }} <strong>{{ customColorsCount }}</strong></p>
          <p class="excel-info">
            {{ $t('preview.excelInfo') }}<br />
            • <strong>{{ $t('preview.excelPixelArt') }}</strong> {{ hasCustomColors ? $t('preview.excelWithCustom') : $t('preview.excelWithoutCustom') }}<br />
            • <strong>{{ $t('preview.excelColorTable') }}</strong> {{ hasCustomColors ? $t('preview.excelColorMapping') : $t('preview.excelSimpleNote') }}
          </p>
        </div>
        
        <div class="preview-images-container">
          <!-- 原始色预览（移动端或无悬浮时显示） -->
          <div class="preview-section" v-if="!isDesktop">
            <h4 class="preview-title">{{ $t('preview.originalPreview') }}</h4>
            <div class="preview-image-container" @mousemove="onHoverOriginal" @mouseleave="hideHover">
              <img
                v-if="originalPreview && !isRendering"
                :src="originalPreview"
                :alt="`${gridSize.width}×${gridSize.height} 原始色像素画预览`"
                class="preview-image"
                @load="onImageLoad"
              />
              <div v-else-if="isRendering" class="loading-preview">
                <div class="progress-bar">
                  <div class="progress-inner" :style="{ width: (renderProgress * 100) + '%' }"></div>
                </div>
                <p>{{ renderMessage }}</p>
              </div>
              <div v-else class="loading-preview">
                <div class="spinner"></div>
                <p>{{ $t('preview.generating') }}</p>
              </div>
            </div>
          </div>
          
          <!-- 相近自定义色预览（移动端或无悬浮时显示） -->
          <div v-if="!isDesktop" class="preview-section">
            <div class="preview-header">
              <h4 class="preview-title">🎨 {{ hasCustomColors ? t('preview.customColorPreview') : t('preview.originalPreviewNoCustom') }}</h4>
            </div>
            <div class="preview-image-container" @mousemove="onHoverCustom" @mouseleave="hideHover" @click="onClickCustom">
              <img v-if="displayImage && !isCustomRendering" :src="displayImage" :alt="`${gridSize.width}×${gridSize.height} 像素画预览`" class="preview-image" @load="onImageLoad" />
              <div v-else-if="isCustomRendering" class="loading-preview">
                <div class="progress-bar">
                  <div class="progress-inner" :style="{ width: (customRenderProgress * 100) + '%' }"></div>
                </div>
                <p>{{ customRenderMessage }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 右侧悬浮：原始色预览 + 相近色预览 -->
        <div v-if="isDesktop" class="right-floating">
          <div class="right-floating-block">
            <h4 class="right-title">🎨 {{ t('preview.originalPreviewNoCustom')}}</h4>
            <div class="preview-image-container compact" @mousemove="onHoverOriginal" @mouseleave="hideHover">
              <img v-if="originalPreview && !isRendering" :src="originalPreview" :alt="`${gridSize.width}×${gridSize.height} 原始色像素画预览`" class="preview-image" @load="onImageLoad" />
              <div v-else-if="isRendering" class="loading-preview">
                <div class="progress-bar"><div class="progress-inner" :style="{ width: (renderProgress * 100) + '%' }"></div></div>
                <p>{{ renderMessage }}</p>
              </div>
              <div v-else class="loading-preview">
                <div class="spinner"></div>
                <p>{{ $t('preview.generating') }}</p>
              </div>
            </div>
          </div>

          <div class="right-floating-block">
            <h4 class="right-title">🎨 {{ t('preview.customColorPreview') }}</h4>
            <div class="preview-image-container compact" @mousemove="onHoverCustom" @mouseleave="hideHover" @click="onClickCustom">
              <img v-if="displayImage && !isCustomRendering" :src="displayImage" :alt="`${gridSize.width}×${gridSize.height} 相近色预览`" class="preview-image" @load="onImageLoad" />
              <div v-else-if="isCustomRendering" class="loading-preview">
                <div class="progress-bar"><div class="progress-inner" :style="{ width: (customRenderProgress * 100) + '%' }"></div></div>
                <p>{{ customRenderMessage }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 工具配置（非悬浮，内联显示） -->
        <div v-if="hasCustomColors" class="tools-panel">
          <button class="btn-secondary tools-toggle" @click="toolsOpen = !toolsOpen">
            {{ toolsOpen ? t('preview.collapseConfig') : t('preview.expandConfig') }}
          </button>
          <transition name="fade">
            <div v-show="toolsOpen" class="tools-grid">
              <div class="tool-item">
                <label for="algorithm">匹配算法</label>
                <select id="algorithm" v-model="selectedAlgorithm" @change="onAlgorithmChange">
                  <option value="rgb">RGB欧几里得距离</option>
                  <option value="lab">CIELAB感知距离</option>
                  <option value="weighted-rgb">加权RGB距离</option>
                  <option value="hsv">HSV色彩空间距离</option>
                  <option value="ciede2000">CIEDE2000工业标准</option>
                </select>
              </div>
              <div class="tool-item">
                <label for="gamma">Gamma</label>
                <div class="row">
                  <input id="gamma" type="range" min="0.5" max="2.5" step="0.05" v-model.number="gamma" @input="refreshDebounced" />
                  <span>{{ gamma.toFixed(2) }}</span>
                </div>
              </div>
              <div class="tool-item">
                <label for="brightness">亮度</label>
                <div class="row">
                  <input id="brightness" type="range" min="-1" max="1" step="0.05" v-model.number="brightness" @input="refreshDebounced" />
                  <span>{{ brightness.toFixed(2) }}</span>
                </div>
              </div>
              <div class="tool-item">
                <label>色彩约束</label>
                <div class="row">
                  <span>高光</span>
                  <input type="range" min="0" max="1" step="0.05" v-model.number="clampHighlights" @input="refreshDebounced" />
                </div>
                <div class="row">
                  <span>阴影</span>
                  <input type="range" min="0" max="1" step="0.05" v-model.number="liftShadows" @input="refreshDebounced" />
                </div>
                <div class="row">
                  <span>饱和</span>
                  <input type="range" min="-1" max="1" step="0.05" v-model.number="saturationBoost" @input="refreshDebounced" />
                </div>
              </div>
              <div class="tool-item">
                <label for="posterize">海报化</label>
                <div class="row">
                  <input id="posterize" type="range" min="0" max="32" step="1" v-model.number="posterizeLevels" @input="refreshDebounced" />
                  <span>{{ posterizeLevels > 1 ? posterizeLevels + ' ' + t('preview.levelsPerChannel') : t('preview.disabled') }}</span>
                </div>
              </div>
              <div class="tool-item">
                <label>抖动</label>
                <div class="row">
                  <input id="ditherEnable" type="checkbox" v-model="enableDither" @change="refreshCustomPreview" />
                  <span>启用</span>
                </div>
                <div class="row">
                  <span>强度</span>
                  <input type="range" min="0" max="1" step="0.05" v-model.number="ditherStrength" @input="refreshDebounced" :disabled="!enableDither" />
                </div>
              </div>
              <div class="tool-item">
                <label>前置处理</label>
                <div class="row">
                  <div class="tool-item">
                    <span>先压缩</span>
                    <div>
                      <input type="range" min="0.1" max="1" step="0.05" v-model.number="downscaleRatio" @input="refreshDebounced" />
                      <span>{{ (downscaleRatio*100).toFixed(0) }}%</span>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="tool-item">
                    <span>降噪</span>
                    <div>
                      <input type="range" min="0" max="1" step="0.05" v-model.number="denoiseStrength" @input="refreshDebounced" />
                      <span>{{ denoiseStrength.toFixed(2) }}</span>
                    </div>
                  </div>
                  
                </div>
                <div class="row">
                  <div class="tool-item">
                    <span>边缘保留</span>
                    <div>
                      <input type="range" min="0" max="1" step="0.05" v-model.number="edgePreserveStrength" @input="refreshDebounced" />
                      <span>{{ edgePreserveStrength.toFixed(2) }}</span>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="tool-item">
                    <span>颜色简化</span>
                    <div>
                      <input type="range" min="0" max="16" step="1" v-model.number="preQuantizeLevels" @input="refreshDebounced" />
                      <span>{{ preQuantizeLevels > 1 ? preQuantizeLevels + t('preview.levelsPerChannel') : t('preview.disabled') }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="tool-item">
                <label>智能模式</label>
                <div class="row" style="gap:.5rem; flex-wrap: wrap;">
                  <button class="btn-secondary" @click="applySmartCartoon" :disabled="isRefreshing || isRendering">漫画模式</button>
                  <button class="btn-secondary" @click="applySmartPhoto" :disabled="isRefreshing || isRendering || !extractedColors">照片模式</button>
                  <button class="btn-secondary" @click="applySmartPixelArt" :disabled="isRefreshing || isRendering">像素风</button>
                  <button class="btn-secondary" @click="applySmartNeon" :disabled="isRefreshing || isRendering">赛博霓虹</button>
                  <button class="btn-secondary" @click="applySmartPastelJP" :disabled="isRefreshing || isRendering">清新日系</button>
                  <button class="btn-secondary" @click="applySmartNES" :disabled="isRefreshing || isRendering">NES/FC 像素风</button>
                </div>
                <div class="smart-hints">
                  <p><strong>说明</strong>：智能模式仅调整渲染参数（算法/抖动/Gamma/亮度/约束/海报化），<u>不会改变当前画风（配色）</u>。</p>
                  <p><strong>像素风</strong>：减少颜色等级（海报化），关闭抖动，边缘清晰。</p>
                  <p><strong>赛博霓虹</strong>：高饱和、高对比，推荐 HSV/CIEDE2000，建议开启轻度抖动。</p>
                  <p><strong>清新日系</strong>：低饱和、稍提亮，柔和观感，推荐 CIEDE2000。</p>
                  <p><strong>NES/FC 像素风</strong>：小色表，高对比，建议低分辨率与海报化/无抖动。</p>
                </div>
              </div>
              <div class="tool-actions">
                <button class="btn-secondary" @click="refreshCustomPreview" :disabled="isRefreshing">
                  <span v-if="isRefreshing" class="loading-spinner"></span>
                  <span v-else>🔄 刷新</span>
                </button>
                <button class="btn-secondary" @click="resetControls" :disabled="isRefreshing">♻️ 重置</button>
              </div>
            </div>
          </transition>
        </div>

        <div class="preview-actions">
          <button 
            class="btn-secondary" 
            @click="() => previewStore.renderAllPreviews()"
            :disabled="isRendering || isCustomRendering"
          >
            <span v-if="isRendering || isCustomRendering" class="loading-spinner"></span>
            {{ (isRendering || isCustomRendering) ? t('preview.regenerating') : t('preview.regenerate') }}
          </button>
          <button class="btn-success" @click="copyOriginalToClipboard" v-if="originalPreview">
            {{ $t('preview.copyOriginalToClipboard') }}
          </button>
          <button 
            class="btn-success" 
            @click="copyDisplayImageToClipboard" 
            v-if="displayImage"
          >
            {{ t('preview.copyToClipboard', { type: hasCustomColors ? t('preview.customColor') : t('preview.originalColor') }) }}
          </button>
        </div>
      </div>
    </div>

    <!-- 修改/删除颜色弹窗 -->
    <div v-if="isColorActionModalOpen" class="modal-overlay" @click.self="closeColorActionModal">
      <div class="modal">
        <h4 class="modal-title">颜色操作</h4>
        <div class="modal-body">
          <div v-if="!hasCustomColors" class="no-custom-colors">
            <p>当前没有自定义颜色</p>
            <p class="text-muted">请先在自定义颜色管理中添加颜色</p>
          </div>
          <div v-else-if="clickedColor" class="color-action-content">
            <div class="clicked-color-info">
              <h5>点击的颜色</h5>
              <div class="color-display">
                <div
                  class="color-swatch large"
                  :style="{ backgroundColor: clickedColor.hex }"
                ></div>
                <div class="color-details">
                  <div class="color-values">
                    <div class="value-row">
                      <span class="label">名称:</span>
                      <span class="value">{{ clickedColor.name || `颜色 #${clickedColor.id}` }}</span>
                    </div>
                    <div class="value-row">
                      <span class="label">RGB:</span>
                      <span class="value">{{ clickedColor.r }}, {{ clickedColor.g }}, {{ clickedColor.b }}</span>
                    </div>
                    <div class="value-row">
                      <span class="label">HEX:</span>
                      <span class="value hex">{{ clickedColor.hex }}</span>
                    </div>
                    <div class="value-row">
                      <span class="label">坐标:</span>
                      <span class="value">({{ clickedGridPos.x }}, {{ clickedGridPos.y }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="action-buttons">
              <button class="btn-primary" @click="openEditColorModal">
                ✎ 修改颜色
              </button>
              <button class="btn-danger" @click="deleteColor">
                🗑️ 删除颜色
              </button>
            </div>
            
            <div class="color-action-hint">
              <p><strong>💡 重要说明：</strong></p>
              <p>修改颜色后，系统会重新根据原图颜色值进行相近色匹配，<strong>不一定会直接修改当前预览位置的像素颜色</strong>。</p>
              <p>这是因为像素画预览是基于原图颜色与自定义颜色的匹配算法生成的，修改某个自定义颜色会影响所有使用该颜色的像素位置。</p>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closeColorActionModal">取消</button>
        </div>
      </div>
    </div>

    <!-- 编辑颜色弹窗 -->
    <div v-if="isEditColorModalOpen" class="modal-overlay" @click.self="closeEditColorModal">
      <div class="modal">
        <h4 class="modal-title">编辑颜色</h4>
        <div class="modal-body">
          <div class="edit-preview">
            <div class="color-swatch" :style="{ backgroundColor: editColorHex }"></div>
            <div class="color-values-display">
              <span class="hex-value">{{ editColorHex }}</span>
              <span class="rgb-value">RGB({{ editColor.r }}, {{ editColor.g }}, {{ editColor.b }})</span>
            </div>
            <input type="color" v-model="editColorPicker" aria-label="选择颜色" />
          </div>
          <div class="color-inputs">
            <div class="color-input-group">
              <label>R:</label>
              <input type="number" v-model.number="editColor.r" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>G:</label>
              <input type="number" v-model.number="editColor.g" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>B:</label>
              <input type="number" v-model.number="editColor.b" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>HEX:</label>
              <div class="hex-input-wrapper">
                <span class="hex-prefix">#</span>
                <input type="text" v-model="editHexNoHash" maxlength="6" @input="syncEditRgbFromHex"
                  class="hex-input" />
              </div>
            </div>
          </div>
          <div class="color-input-group" style="margin-top: .5rem;">
            <label>名称:</label>
            <input type="text" v-model="editName" placeholder="颜色名称" />
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closeEditColorModal">取消</button>
          <button class="btn-success" @click="saveEditColor" :disabled="!isEditValid">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColorStore } from '../stores/colorStore'
import { usePreviewStore } from '../stores/previewStore'
import { generateExcelPixelArt, generatePaletteFromExtractedColors, rgbToHex, hexToRgb, isValidColor } from '../utils/colorUtils'

const { t } = useI18n()

const colorStore = useColorStore()
const previewStore = usePreviewStore()

const pixelSize = ref(colorStore.previewPixelSize)
const scale = ref(1)

// 使用统一的预览管理
const isRendering = computed(() => previewStore.isRendering)
const renderProgress = computed(() => previewStore.renderProgress)
const renderMessage = computed(() => previewStore.renderMessage)
const originalPreview = computed(() => previewStore.originalPreview)
const customPreview = computed(() => previewStore.customPreview)

// 相近色预览图专用状态
const isCustomRendering = computed(() => previewStore.isCustomRendering)
const customRenderProgress = computed(() => previewStore.customRenderProgress)
const customRenderMessage = computed(() => previewStore.customRenderMessage)

// 与全局算法保持同步：下拉框直接双向绑定到store值
const selectedAlgorithm = computed({
  get: () => colorStore.colorMatchingAlgorithm,
  set: (val: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000') => {
    colorStore.setColorMatchingAlgorithm(val)
  }
})

// 桌面端判定
const isDesktop = ref(window.innerWidth >= 1024)
function handleResize() {
  isDesktop.value = window.innerWidth >= 1024
}
onMounted(() => {
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// 加载状态
const isRefreshing = ref(false)
// isRegenerating 已移至 previewStore 统一管理
// 抖动选项（仅对相近色预览生效）
const enableDither = ref(false)
const ditherStrength = ref(0.8)
const posterizeLevels = ref(0)

// 归一化与色彩约束参数
const gamma = ref(1.0)
const brightness = ref(0)
const clampHighlights = ref(0)
const liftShadows = ref(0)
const saturationBoost = ref(0)
const toolsOpen = ref(false)

// 前处理参数
const downscaleRatio = ref(1.0)
const denoiseStrength = ref(0.5)
const edgePreserveStrength = ref(0.8)
const preQuantizeLevels = ref(0)

const hasImage = computed(() => colorStore.hasImage)
const hasExtractedColors = computed(() => !!colorStore.extractedColors)
const canGeneratePreview = computed(() => colorStore.canGeneratePreview)
const gridSize = computed(() => colorStore.gridSize)
const extractedColors = computed(() => colorStore.previewExtractedColors)
const customColors = computed(() => colorStore.customColors)
const hasCustomColors = computed(() => customColors.value.length > 0)
const customColorsCount = computed(() => customColors.value.length)
const isExtracting = computed(() => colorStore.isExtracting)

// 预览图状态（已移至previewStore统一管理）
// 悬浮命中时按需读取矩阵，避免对整张矩阵建立响应依赖
// const matchedGridOriginal = ref<Array<Array<{ hex: string; r: number; g: number; b: number }>> | null>(null)
// const matchedGridCustom = ref<Array<Array<{ id: number; name: string; hex: string; r: number; g: number; b: number }>> | null>(null)

// 智能显示图片：如果有自定义颜色显示相近色，否则显示原始色
const displayImage = computed(() => {
  if (isRendering.value || isCustomRendering.value) return ''
  if (customPreview.value) return customPreview.value
  return originalPreview.value
})

const previewWidth = computed(() => extractedColors.value ? extractedColors.value.width * pixelSize.value * scale.value : 0)
const previewHeight = computed(() => extractedColors.value ? extractedColors.value.height * pixelSize.value * scale.value : 0)

// 合并所有预览相关的监听器，避免重复触发
watch([
  pixelSize, 
  scale, 
  () => `${colorStore.gridSize.width}x${colorStore.gridSize.height}`, // 只监听尺寸字符串，避免deep监听
  () => colorStore.previewPixelSize
], ([newPixelSize, newScale, newGridSizeStr, newPreviewPixelSize], [oldPixelSize, oldScale, oldGridSizeStr, oldPreviewPixelSize]) => {
  // 同步全局预览像素大小
  if (newPreviewPixelSize !== oldPreviewPixelSize) {
    pixelSize.value = newPreviewPixelSize
  }
  
  // 其他变化触发预览更新
  if (canGeneratePreview.value && (
    newPixelSize !== oldPixelSize || 
    newScale !== oldScale || 
    newGridSizeStr !== oldGridSizeStr
  )) {
    updatePreview()
  }
})

// 监听自定义颜色变化，自动刷新相近色预览
// watch(customColors, async () => {
//   if (canGeneratePreview.value && hasCustomColors.value && !isRefreshing.value) {
//     await refreshCustomPreview()
//   }
// }, { deep: true })

watch(isExtracting, (val) => {
  if (val) {
    // 清空当前预览，展示加载
    previewStore.resetPreviews()
  } else {
    // 提取完成后重新生成
    if (canGeneratePreview.value) {
      previewStore.renderAllPreviews()
    }
  }
})

async function updatePreview() {
  if (!extractedColors.value) return


  // 使用统一的预览管理
  await previewStore.updatePreviewOptions({
    pixelSize: pixelSize.value * scale.value,
    algorithm: selectedAlgorithm.value,
    dithering: enableDither.value ? 'floyd-steinberg' : 'none',
    strength: ditherStrength.value,
    gamma: gamma.value,
    brightness: brightness.value,
    constraints: {
      clampHighlights: clampHighlights.value,
      liftShadows: liftShadows.value,
      saturationBoost: saturationBoost.value
    },
    posterizeLevels: posterizeLevels.value,
    downscaleRatio: downscaleRatio.value,
    denoiseStrength: denoiseStrength.value,
    edgePreserveStrength: edgePreserveStrength.value,
    preQuantizeLevels: preQuantizeLevels.value,
  })
}

// regeneratePreview 方法已移至 previewStore 统一管理

async function refreshCustomPreview() {
  if (!extractedColors.value || !hasCustomColors.value || isRefreshing.value) return
  isRefreshing.value = true
  
  // 使用统一的预览管理 - 只渲染相近色预览图
  await previewStore.renderCustomPreviewOnly({
    pixelSize: pixelSize.value * scale.value,
    algorithm: selectedAlgorithm.value,
    dithering: enableDither.value ? 'floyd-steinberg' : 'none',
    strength: ditherStrength.value,
    gamma: gamma.value,
    brightness: brightness.value,
    constraints: {
      clampHighlights: clampHighlights.value,
      liftShadows: liftShadows.value,
      saturationBoost: saturationBoost.value
    },
    posterizeLevels: posterizeLevels.value,
    downscaleRatio: downscaleRatio.value,
    denoiseStrength: denoiseStrength.value,
    edgePreserveStrength: edgePreserveStrength.value,
    preQuantizeLevels: preQuantizeLevels.value,
  })
  
  isRefreshing.value = false
}

async function onAlgorithmChange() {
  // 颜色匹配算法改变后，刷新相近色预览
  if (hasCustomColors.value) {
    await refreshCustomPreview()
  }
}

// 当画风应用时，store中的算法会被更新。
// 监听store算法变化，同步刷新相近色预览。
// watch(() => colorStore.colorMatchingAlgorithm, async () => {
//   if (hasCustomColors.value && canGeneratePreview.value) {
//     await refreshCustomPreview()
//   }
// })

function downloadOriginalPreview() {
  if (!originalPreview.value) return
  
  const link = document.createElement('a')
  link.download = `pixel-art-original-${gridSize.value.width}x${gridSize.value.height}.png`
  link.href = originalPreview.value
  link.click()
}

function downloadDisplayImage() {
  if (!displayImage.value) return
  
  const link = document.createElement('a')
  const type = hasCustomColors.value ? 'custom' : 'original'
  link.download = `pixel-art-${type}-${gridSize.value.width}x${gridSize.value.height}.png`
  link.href = displayImage.value
  link.click()
}

// 基于 previewStore 的 base64 读取像素（不依赖 DOM）
async function getCurrentPreviewPixels() {
  const base64 = customPreview.value || originalPreview.value
  if (!base64) return extractedColors.value?.pixels || []

  const { width, height } = gridSize.value
  const img = new Image()
  // dataURL 同源，无需跨域设置
  const pixels: Array<Array<{ r: number; g: number; b: number; hex: string }>> = []

  await new Promise<void>((resolve) => {
    img.onload = () => resolve()
    img.src = base64
  })

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = width
  canvas.height = height
  // 将预览图按网格尺寸绘制，得到网格级像素
  ctx.drawImage(img, 0, 0, width, height)

  for (let y = 0; y < height; y++) {
    pixels[y] = []
    for (let x = 0; x < width; x++) {
      const d = ctx.getImageData(x, y, 1, 1).data
      const r = d[0], g = d[1], b = d[2]
      const hex = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`
      pixels[y][x] = { r, g, b, hex }
    }
  }

  return pixels
}

async function downloadExcelPixelArt() {
  if (!extractedColors.value) {
    alert(t('preview.uploadImageFirst'))
    return
  }
  try {
    // 导出进度 UI
    const progressBar = document.createElement('div')
    progressBar.style.position = 'fixed'
    progressBar.style.left = '50%'
    progressBar.style.top = '20px'
    progressBar.style.transform = 'translateX(-50%)'
    progressBar.style.width = '360px'
    progressBar.style.padding = '8px'
    progressBar.style.background = 'rgba(0,0,0,0.7)'
    progressBar.style.color = '#fff'
    progressBar.style.borderRadius = '8px'
    progressBar.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)'
    progressBar.style.zIndex = '2000'
    const bar = document.createElement('div')
    bar.style.height = '6px'
    bar.style.background = '#28a745'
    bar.style.width = '0%'
    bar.style.borderRadius = '4px'
    bar.style.transition = 'width .15s ease'
    const text = document.createElement('div')
    text.style.fontSize = '12px'
    text.style.marginTop = '6px'
    text.innerText = t('preview.preparing')
    const track = document.createElement('div')
    track.style.height = '6px'
    track.style.background = 'rgba(255,255,255,0.25)'
    track.style.borderRadius = '4px'
    track.appendChild(bar)
    progressBar.appendChild(track)
    progressBar.appendChild(text)
    document.body.appendChild(progressBar)

    const onProgress = (p: number, msg?: string) => {
      const pct = Math.max(0, Math.min(1, p))
      bar.style.width = (pct * 100).toFixed(0) + '%'
      if (msg) text.innerText = `${msg} ${(pct * 100).toFixed(0)}%`
    }

    // 直接基于 previewStore 的 base64 读取像素（包含画风与归并后的最终效果）
    const currentPreviewData = await getCurrentPreviewPixels()
    
    const excelBlob = await generateExcelPixelArt(
      currentPreviewData,
      hasCustomColors.value ? customColors.value.map(c => ({ r: c.r, g: c.g, b: c.b, hex: c.hex, name: c.name || `颜色${c.id}`, id: c.id })) : [],
      selectedAlgorithm.value,
      { 
        onProgress, 
        baseFabricColor: colorStore.baseFabricColor || undefined,
        labels: {
          sheetPixelArt: t('excel.sheetPixelArt'),
          sheetPalette: t('excel.sheetPalette'),
          sheetInfo: t('excel.sheetInfo'),
          sheetSymbols: t('excel.sheetSymbols'),
          colIndex: t('excel.colIndex'),
          colColorName: t('excel.colColorName'),
          colSymbol: t('excel.colSymbol'),
          colHex: t('excel.colHex'),
          colRgb: t('excel.colRgb'),
          colUsage: t('excel.colUsage'),
          colPercent: t('excel.colPercent'),
          colCoverage: t('excel.colCoverage'),
          colEstimate: t('excel.colEstimate'),
          colPreview: t('excel.colPreview'),
          colRemark: t('excel.colRemark'),
          remarkBackground: t('excel.remarkBackground'),
          userParams: t('excel.userParams'),
          stitchPerStrand: t('excel.stitchPerStrand'),
          stitchHint: t('excel.stitchHint'),
          making: t('excel.making'),
          done: t('excel.done'),
          infoTitle: t('excel.infoTitle'),
          infoSection1: t('excel.infoSection1'),
          info1a: t('excel.info1a'),
          info1b: t('excel.info1b'),
          info1c: t('excel.info1c'),
          info1d: t('excel.info1d'),
          info1e: t('excel.info1e'),
          infoSection2: t('excel.infoSection2'),
          info2a: t('excel.info2a'),
          info2b: t('excel.info2b'),
          info2c: t('excel.info2c'),
          info2d: t('excel.info2d'),
          info2e: t('excel.info2e'),
          info2f: t('excel.info2f'),
          info2g: t('excel.info2g'),
          info2h: t('excel.info2h'),
          info2i: t('excel.info2i'),
          info2j: t('excel.info2j'),
          info2k: t('excel.info2k'),
          infoSection3: t('excel.infoSection3'),
          info3a: t('excel.info3a'),
          info3b: t('excel.info3b'),
          info3c: t('excel.info3c'),
          infoSection4: t('excel.infoSection4'),
          info4a: t('excel.info4a'),
          info4b: t('excel.info4b'),
          info4c: t('excel.info4c'),
          infoSection5: t('excel.infoSection5'),
          info5a: t('excel.info5a'),
          info5b: t('excel.info5b'),
          info5c: t('excel.info5c'),
          info5d: t('excel.info5d'),
          info5e: t('excel.info5e'),
          infoSection6: t('excel.infoSection6'),
          info6a: t('excel.info6a'),
          info6b: t('excel.info6b'),
          info6c: t('excel.info6c'),
          info6d: t('excel.info6d')
        }
      }
    )

    const url = URL.createObjectURL(excelBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `pixel-art-excel-${gridSize.value.width}x${gridSize.value.height}.xlsx`
    link.click()
    URL.revokeObjectURL(url)
    document.body.removeChild(progressBar)
  } catch (e) {
    console.error(t('preview.excelGenerationFailed'), e)
    alert(t('preview.excelGenerationFailedRetry'))
  }
}

async function copyOriginalToClipboard() {
  if (!originalPreview.value) return
  
  try {
    const response = await fetch(originalPreview.value)
    const blob = await response.blob()
    
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob
      })
    ])
    
    alert(t('preview.originalCopiedToClipboard'))
  } catch (error) {
    console.error(t('preview.copyFailed'), error)
    alert(t('preview.copyFailedManual'))
  }
}

async function copyDisplayImageToClipboard() {
  if (!displayImage.value) return
  
  try {
    const response = await fetch(displayImage.value)
    const blob = await response.blob()
    
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob
      })
    ])
    
    const type = hasCustomColors.value ? t('preview.customColor') : t('preview.originalColor')
    alert(t('preview.imageCopiedToClipboard', { type }))
  } catch (error) {
    console.error(t('preview.copyFailed'), error)
    alert(t('preview.copyFailedManual'))
  }
}

function onImageLoad() {
  // 图片加载完成后的处理
}

// 简易防抖
function debounce<T extends (...args: any[]) => any>(fn: T, wait = 250) {
  let t: number | undefined
  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t)
    t = window.setTimeout(() => fn(...args), wait)
  }
}

const refreshDebounced = debounce(async () => {
  if (!isRefreshing.value) {
    await refreshCustomPreview()
  }
}, 250)

async function resetControls() {
  gamma.value = 1.0
  brightness.value = 0
  clampHighlights.value = 0
  liftShadows.value = 0
  saturationBoost.value = 0
  enableDither.value = false
  ditherStrength.value = 0.8
  posterizeLevels.value = 0
  downscaleRatio.value = 1.0
  denoiseStrength.value = 0.5
  edgePreserveStrength.value = 0.8
  preQuantizeLevels.value = 0
  await refreshCustomPreview()
}

async function applySmartCartoon() {
  // 调整渲染参数，不改变画风（配色）
  isRefreshing.value = true
  try {
    selectedAlgorithm.value = 'weighted-rgb'
    enableDither.value = false
    gamma.value = 1.0
    brightness.value = 0
    clampHighlights.value = 0
    liftShadows.value = 0
    saturationBoost.value = 0.2
    posterizeLevels.value = 0
    
    // 只渲染相近色预览图
    await previewStore.renderCustomPreviewOnly({
      pixelSize: pixelSize.value * scale.value,
      algorithm: selectedAlgorithm.value,
      dithering: enableDither.value ? 'floyd-steinberg' : 'none',
      strength: ditherStrength.value,
      gamma: gamma.value,
      brightness: brightness.value,
      constraints: {
        clampHighlights: clampHighlights.value,
        liftShadows: liftShadows.value,
        saturationBoost: saturationBoost.value
      },
      posterizeLevels: posterizeLevels.value,
      downscaleRatio: downscaleRatio.value,
      denoiseStrength: denoiseStrength.value,
      edgePreserveStrength: edgePreserveStrength.value,
      preQuantizeLevels: preQuantizeLevels.value,
    })
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

async function applySmartPhoto() {
  if (!extractedColors.value) return
  isRefreshing.value = true
  try {
    // 自动生成 LAB KMeans 调色板（此模式会替换配色，属于例外）
    const palette = await generatePaletteFromExtractedColors(
      extractedColors.value.pixels,
      32,
      { sampleRate: 0.3 }
    )
    colorStore.clearCustomColors()
    for (const c of palette) {
      colorStore.addCustomColor({ r: c.r, g: c.g, b: c.b, hex: c.hex, name: c.name })
    }
    selectedAlgorithm.value = 'ciede2000'
    enableDither.value = true
    ditherStrength.value = 0.6
    gamma.value = 1.05
    brightness.value = 0
    clampHighlights.value = 0.2
    liftShadows.value = 0.1
    saturationBoost.value = 0.05
    posterizeLevels.value = 0
    await refreshCustomPreview()
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

// 像素风：强调低色阶与硬边（不改配色）
async function applySmartPixelArt() {
  isRefreshing.value = true
  try {
    selectedAlgorithm.value = 'weighted-rgb'
    enableDither.value = false
    ditherStrength.value = 0.0
    gamma.value = 1.0
    brightness.value = 0
    clampHighlights.value = 0
    liftShadows.value = 0
    saturationBoost.value = 0.05
    posterizeLevels.value = 6
    await refreshCustomPreview()
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

// 赛博霓虹：高饱和、高对比、偏冷光感（不改配色）
async function applySmartNeon() {
  isRefreshing.value = true
  try {
    selectedAlgorithm.value = 'hsv'
    enableDither.value = true
    ditherStrength.value = 0.4
    gamma.value = 1.0
    brightness.value = 0.05
    clampHighlights.value = 0.25
    liftShadows.value = 0.0
    saturationBoost.value = 0.35
    posterizeLevels.value = 0
    await refreshCustomPreview()
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

// 清新日系：低饱和、略提亮、柔和（不改配色）
async function applySmartPastelJP() {
  isRefreshing.value = true
  try {
    selectedAlgorithm.value = 'ciede2000'
    enableDither.value = false
    ditherStrength.value = 0
    gamma.value = 1.1
    brightness.value = 0.08
    clampHighlights.value = 0.05
    liftShadows.value = 0.05
    saturationBoost.value = -0.15
    posterizeLevels.value = 0
    await refreshCustomPreview()
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

// NES/FC 像素风：若无配色则自动应用 nes 调色板
async function applySmartNES() {
  isRefreshing.value = true
  try {
    if (customColors.value.length === 0) {
      // 自动套用 NES 画风（只在没有调色板时）
      colorStore.applyArtStyle && colorStore.applyArtStyle('nes')
    }
    // 参数设置：小色表强调硬边，低色阶，无抖动
    selectedAlgorithm.value = 'weighted-rgb'
    enableDither.value = false
    ditherStrength.value = 0
    gamma.value = 1.05
    brightness.value = 0.02
    clampHighlights.value = 0.1
    liftShadows.value = 0
    saturationBoost.value = 0.1
    posterizeLevels.value = 5
    await refreshCustomPreview()
  } catch (e) {
    console.error(e)
  } finally {
    isRefreshing.value = false
  }
}

// 悬浮提示状态
const hover = ref<{ visible: boolean; x: number; y: number; hex: string; title: string; extra?: string }>({
  visible: false,
  x: 0,
  y: 0,
  hex: '#000000',
  title: '',
  extra: ''
})

// 点击处理相关状态
const isColorActionModalOpen = ref(false)
const isEditColorModalOpen = ref(false)
const clickedColor = ref<{ id: number; r: number; g: number; b: number; hex: string; name?: string } | null>(null)
const clickedGridPos = ref({ x: 0, y: 0 })

// 编辑颜色相关状态
const editColor = ref({ r: 0, g: 0, b: 0 })
const editName = ref('')
const editColorHex = computed(() => rgbToHex(editColor.value.r, editColor.value.g, editColor.value.b))
const editColorPicker = computed({
  get: () => editColorHex.value,
  set: (val: string) => {
    const rgb = hexToRgb(val)
    if (rgb) {
      editColor.value = { r: rgb.r, g: rgb.g, b: rgb.b }
    }
  }
})
const editHexNoHash = computed({
  get: () => editColorHex.value.replace('#', ''),
  set: (v: string) => {
    const clean = v.replace('#', '')
    if (/^[0-9A-Fa-f]{0,6}$/.test(clean)) {
      const padded = clean.padEnd(6, '0')
      const rgb = hexToRgb('#' + padded)
      if (rgb) {
        editColor.value = { r: rgb.r, g: rgb.g, b: rgb.b }
      }
    }
  }
})
const isEditValid = computed(() => isValidColor(editColor.value.r, editColor.value.g, editColor.value.b))

function hideHover() {
  hover.value.visible = false
  // 重置上次命中，确保下次进入时强制更新
  lastGXOriginal = lastGYOriginal = -1
  lastGXCustom = lastGYCustom = -1
}

function computeGridXY(evt: MouseEvent): { gx: number; gy: number; ok: boolean; pageX: number; pageY: number } {
  if (!extractedColors.value) return { gx: 0, gy: 0, ok: false, pageX: evt.pageX, pageY: evt.pageY }
  const target = (evt.currentTarget as HTMLElement | null)
  if (!target) return { gx: 0, gy: 0, ok: false, pageX: evt.pageX, pageY: evt.pageY }
  const rect = target.getBoundingClientRect()
  const relX = evt.clientX - rect.left
  const relY = evt.clientY - rect.top
  const w = rect.width
  const h = rect.height
  // 图片按 pixelSize*scale 渲染，容器尺寸即为预览尺寸
  const gridW = extractedColors.value.width
  const gridH = extractedColors.value.height
  if (w <= 0 || h <= 0) return { gx: 0, gy: 0, ok: false, pageX: evt.pageX, pageY: evt.pageY }
  const gx = Math.floor(relX / w * gridW)
  const gy = Math.floor(relY / h * gridH)
  if (gx < 0 || gy < 0 || gx >= gridW || gy >= gridH) return { gx, gy, ok: false, pageX: evt.clientX, pageY: evt.clientY }
  return { gx, gy, ok: true, pageX: evt.clientX, pageY: evt.clientY }
}

let lastHoverTimeOriginal = 0
let lastGXOriginal = -1
let lastGYOriginal = -1
function onHoverOriginal(evt: MouseEvent) {
  if (!extractedColors.value) return
  const now = performance.now()
  const pos = computeGridXY(evt)
  if (!pos.ok) { hideHover(); return }
  const fastMove = (now - lastHoverTimeOriginal) < 16
  // 位置及时更新
  hover.value.x = pos.pageX
  hover.value.y = pos.pageY
  // 内容仅在网格坐标变化或超出节流间隔时更新
  if (fastMove && pos.gx === lastGXOriginal && pos.gy === lastGYOriginal) {
    hover.value.visible = true
    return
  }
  const grid = colorStore.extractedColors?.pixels
  if (!grid) { hideHover(); return }
  const c = grid[pos.gy]?.[pos.gx]
  if (!c) { hideHover(); return }
  hover.value.visible = true
  hover.value.hex = c.hex
  hover.value.title = t('preview.originalColor')
  hover.value.extra = `RGB(${c.r}, ${c.g}, ${c.b})`
  lastGXOriginal = pos.gx
  lastGYOriginal = pos.gy
  lastHoverTimeOriginal = now
}

let lastHoverTimeCustom = 0
let lastGXCustom = -1
let lastGYCustom = -1
function onHoverCustom(evt: MouseEvent) {
  if (!extractedColors.value) return
  const now = performance.now()
  const pos = computeGridXY(evt)
  if (!pos.ok) { hideHover(); return }
  const fastMove = (now - lastHoverTimeCustom) < 16
  // 位置及时更新
  hover.value.x = pos.pageX
  hover.value.y = pos.pageY
    // 内容仅在网格坐标变化或超出节流间隔时更新
    if (fastMove && pos.gx === lastGXCustom && pos.gy === lastGYCustom) {
      hover.value.visible = true
      return
    }
    const matrix = colorStore.previewExtractedColors?.pixels
    if (!matrix) { hideHover(); return }
    const chosen = matrix[pos.gy]?.[pos.gx]
    if (!chosen) { hideHover(); return }
    hover.value.visible = true
    hover.value.hex = chosen.hex
    hover.value.title = chosen.name || t('preview.customColor')
    hover.value.extra = `ID #${chosen.id}  RGB(${chosen.r}, ${chosen.g}, ${chosen.b})`
    lastGXCustom = pos.gx
    lastGYCustom = pos.gy
    lastHoverTimeCustom = now
}

// 点击自定义颜色预览图处理
function onClickCustom(evt: MouseEvent) {
  if (!extractedColors.value || !hasCustomColors.value) {
    // 没有自定义颜色时，直接显示提示
    isColorActionModalOpen.value = true
    clickedColor.value = null
    return
  }

  const pos = computeGridXY(evt)
  if (!pos.ok) return

  // 获取点击位置的颜色信息
  const matrix = colorStore.previewExtractedColors?.pixels
  if (!matrix) return

  const chosen = matrix[pos.gy]?.[pos.gx]
  if (!chosen) return

  // 找到对应的自定义颜色
  const customColor = customColors.value.find(c => c.id === chosen.id)
  if (!customColor) return

  // 设置点击的颜色和位置
  clickedColor.value = customColor
  clickedGridPos.value = { x: pos.gx, y: pos.gy }
  isColorActionModalOpen.value = true
}

// 关闭颜色操作弹窗
function closeColorActionModal() {
  isColorActionModalOpen.value = false
  clickedColor.value = null
}

// 打开编辑颜色弹窗
function openEditColorModal() {
  if (!clickedColor.value) return
  
  editColor.value = { r: clickedColor.value.r, g: clickedColor.value.g, b: clickedColor.value.b }
  editName.value = clickedColor.value.name || ''
  isEditColorModalOpen.value = true
}

// 关闭编辑颜色弹窗
function closeEditColorModal() {
  isEditColorModalOpen.value = false
}

// 同步编辑颜色的十六进制值
function syncEditHexFromRgb() {
  // computed editColorHex will reflect automatically
}

// 同步编辑颜色的RGB值
function syncEditRgbFromHex() {
  // handled in editHexNoHash setter
}

// 保存编辑的颜色
function saveEditColor() {
  if (!isEditValid.value || !clickedColor.value) return
  
  const hex = editColorHex.value
  colorStore.updateCustomColor(clickedColor.value.id, {
    r: editColor.value.r,
    g: editColor.value.g,
    b: editColor.value.b,
    hex,
    name: editName.value.trim() || undefined
  })
  
  closeEditColorModal()
  closeColorActionModal()
}

// 删除颜色
function deleteColor() {
  if (!clickedColor.value) return
  
  if (confirm(t('preview.confirmDeleteColor', { name: clickedColor.value.name || t('preview.colorWithId', { id: clickedColor.value.id }) }))) {
    colorStore.removeCustomColor(clickedColor.value.id)
    closeColorActionModal()
  }
}
</script>

<style scoped>
.no-preview {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

/* 移除右侧预留空隙，避免挤占预览区域 */

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.preview-controls {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-group label {
  font-weight: 500;
  color: #495057;
}

.control-group select {
  min-width: 100px;
}



.preview-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-images-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-title {
  margin: 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.preview-controls-group { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.5rem 1rem; align-items: center; justify-items: end; }
.preview-controls-group .algorithm-selector { justify-self: start; max-width: 100%; }
.preview-controls-group .algorithm-selector label { min-width: 64px; text-align: right; }
.preview-controls-group .algorithm-selector input[type="range"] { width: 140px; }
.preview-controls-group .refresh-btn { justify-self: end; }
@media (max-width: 768px) {
  .preview-controls-group { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 480px) {
  .preview-controls-group { grid-template-columns: 1fr; justify-items: stretch; }
  .preview-controls-group .algorithm-selector { justify-content: space-between; }
  .preview-controls-group .refresh-btn { width: 100%; }
}

.algorithm-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.algorithm-selector label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
  white-space: nowrap;
}

.algorithm-selector select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  min-width: 140px;
}

.refresh-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.download-controls {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}



.preview-info {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #667eea;
}

.preview-info p {
  margin: 0.25rem 0;
  color: #495057;
}

.preview-info strong {
  color: #667eea;
}

.excel-info {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 4px solid #17a2b8;
  font-size: 0.875rem;
  line-height: 1.4;
}

.excel-info strong {
  color: #17a2b8;
}

.preview-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.preview-image {
  max-width: 100%;
  max-height: 500px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.loading-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.progress-bar {
  width: 200px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
}

.progress-inner {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.3s ease;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.preview-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.preview-actions button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.preview-header { flex-wrap: wrap; }
.preview-title { flex: 1 1 auto; min-width: 200px; }
.preview-controls-group { flex: 1 1 320px; justify-content: flex-end; align-items: center; gap: 0.75rem 1rem; flex-wrap: wrap; }
.algorithm-selector { white-space: nowrap; }
.algorithm-selector input[type="range"] { width: 130px; }
.algorithm-selector input[type="checkbox"] { transform: translateY(1px); }

.tools-panel { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 0.75rem; }
.tools-toggle { margin-bottom: 0.5rem; }
.tools-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.75rem 1rem; }
.tool-item { display: flex; flex-direction: column; gap: 0.5rem; }
.tool-item .row { display: flex; align-items: center; gap: 0.5rem; }
.tool-actions { display: flex; gap: 0.75rem; align-items: center; justify-content: flex-end; grid-column: 1 / -1; }
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* 悬浮工具面板（桌面端） */
.tools-panel.floating {
  position: fixed;
  right: 16px;
  top: 120px;
  width: 360px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  z-index: 999;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  padding: 12px;
}
.right-floating { position: fixed; right: 16px; top: 120px; width: 360px; max-height: calc(100vh - 160px); overflow: hidden; z-index: 999; display: flex; flex-direction: column; gap: 12px; }
.right-floating .right-floating-block { background: #fff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.15); padding: 12px; border: 1px solid #e9ecef; }
.right-floating .right-title { margin: 0 0 8px 0; font-size: 1rem; color: #495057; }
.preview-image-container.compact { min-height: 120px; max-height: 42vh; display: flex; align-items: center; justify-content: center; }
.right-floating .preview-image { max-width: 100%; height: auto; max-height: 33vh; object-fit: contain; }
.tools-panel .tools-toggle {
  width: 100%;
}

/* 移动端回退为内联 */
@media (max-width: 1023px) {
  .tools-panel.floating {
    position: static;
    width: auto;
    max-height: none;
    box-shadow: none;
    right: auto;
    top: auto;
    margin-top: 0.5rem;
  }
  .right-floating { position: static; width: auto; max-height: none; box-shadow: none; }
}

/* 悬浮提示样式 */
.hover-tooltip {
  position: fixed;
  pointer-events: none;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  background: rgba(255,255,255,0.95);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
  z-index: 2000;
  min-width: 160px;
  transform: translate(12px, 12px);
}
.hover-tooltip .swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.1);
}
.hover-tooltip .meta { line-height: 1.2; }
.hover-tooltip .meta .name { font-weight: 600; font-size: 12px; }
.hover-tooltip .meta .hex, .hover-tooltip .meta .extra { font-size: 11px; color: #6c757d; }

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #fff;
  border-radius: 8px;
  width: 520px;
  max-width: 92vw;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-title {
  margin: 0;
  padding: 1rem 1.25rem 0 1.25rem;
  color: #343a40;
}

.modal-body {
  padding: 1rem 1.25rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem 1.25rem;
}

.no-custom-colors {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.text-muted {
  opacity: 0.7;
  font-size: 0.875rem;
}

.color-action-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.clicked-color-info h5 {
  margin-bottom: 1rem;
  color: #495057;
}

.color-display {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.color-swatch {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.color-swatch.large {
  width: 80px;
  height: 80px;
}

.color-details {
  flex: 1;
}

.color-values {
  margin-bottom: 1.5rem;
}

.value-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.label {
  font-weight: 500;
  color: #495057;
  min-width: 60px;
}

.value {
  font-family: monospace;
  color: #333;
}

.value.hex {
  font-weight: 600;
  color: #667eea;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.color-action-hint {
  margin-top: 1rem;
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  border-left: 4px solid #f39c12;
}

.color-action-hint p {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #856404;
}

.color-action-hint p:first-child {
  margin-top: 0;
}

.color-action-hint p:last-child {
  margin-bottom: 0;
}

.btn-danger {
  background: #dc3545;
  color: white;
  border: 1px solid #dc3545;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-danger:hover {
  background: #c82333;
  border-color: #bd2130;
}

.edit-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.color-inputs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.color-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.color-input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

.color-input-group input {
  width: 80px;
  text-align: center;
}

.hex-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

.hex-prefix {
  background: #f8f9fa;
  padding: 0.5rem 0.75rem;
  border-right: 1px solid #ced4da;
  color: #6c757d;
  font-weight: 500;
  font-family: monospace;
}

.hex-input {
  border: none;
  padding: 0.5rem 0.75rem;
  width: 120px;
  font-family: monospace;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.hex-input:focus {
  outline: none;
  background-color: #fff;
}

.color-values-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rgb-value {
  font-size: 0.875rem;
  color: #6c757d;
  font-family: monospace;
}

.hex-value {
  font-family: monospace;
  font-weight: 600;
  color: #495057;
}
</style>
